import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Spinner from '../../components/Spinner';
import './AdminPages.css';

function AdminPages() {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    content: '',
    meta_description: '',
    published: true
  });
  const [faqData, setFaqData] = useState(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/pages/admin/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load pages');
      setLoading(false);
      console.error(err);
    }
  };

  const handleSelectPage = (page) => {
    setSelectedPage(page);
    setFormData({
      slug: page.slug,
      title: page.title,
      content: page.content,
      meta_description: page.meta_description || '',
      published: Boolean(page.published)
    });

    // Parse FAQ JSON if this is a FAQ page
    if (page.content_type === 'faq_json') {
      try {
        setFaqData(JSON.parse(page.content));
      } catch (e) {
        console.error('Failed to parse FAQ JSON:', e);
        setFaqData(null);
      }
    } else {
      setFaqData(null);
    }

    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (selectedPage) {
      setFormData({
        slug: selectedPage.slug,
        title: selectedPage.title,
        content: selectedPage.content,
        meta_description: selectedPage.meta_description || '',
        published: Boolean(selectedPage.published)
      });

      // Reset FAQ data
      if (selectedPage.content_type === 'faq_json') {
        try {
          setFaqData(JSON.parse(selectedPage.content));
        } catch (e) {
          console.error('Failed to parse FAQ JSON:', e);
        }
      }
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');

      // If it's FAQ, convert faqData to JSON string
      const contentToSave = selectedPage.content_type === 'faq_json'
        ? JSON.stringify(faqData, null, 2)
        : formData.content;

      const response = await axios.put(
        `/api/pages/admin/${selectedPage.id}`,
        {
          ...formData,
          content: contentToSave,
          published: formData.published ? 1 : 0
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSelectedPage(response.data);
      setIsEditing(false);
      fetchPages(); // Refresh list
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save page');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      content: value
    }));
  };

  // FAQ editor handlers
  const handleFaqSectionChange = (sectionIndex, field, value) => {
    setFaqData(prev => {
      const newSections = [...prev.sections];
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        [field]: value
      };
      return { ...prev, sections: newSections };
    });
  };

  const handleFaqQuestionChange = (sectionIndex, questionIndex, field, value) => {
    setFaqData(prev => {
      const newSections = [...prev.sections];
      const newQuestions = [...newSections[sectionIndex].questions];
      newQuestions[questionIndex] = {
        ...newQuestions[questionIndex],
        [field]: value
      };
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        questions: newQuestions
      };
      return { ...prev, sections: newSections };
    });
  };

  const handleAddQuestion = (sectionIndex) => {
    setFaqData(prev => {
      const newSections = [...prev.sections];
      newSections[sectionIndex].questions.push({
        question: 'New question?',
        answer: 'New answer.'
      });
      return { ...prev, sections: newSections };
    });
  };

  const handleDeleteQuestion = (sectionIndex, questionIndex) => {
    setFaqData(prev => {
      const newSections = [...prev.sections];
      newSections[sectionIndex].questions = newSections[sectionIndex].questions.filter(
        (_, idx) => idx !== questionIndex
      );
      return { ...prev, sections: newSections };
    });
  };

  const handleAddSection = () => {
    setFaqData(prev => ({
      ...prev,
      sections: [
        ...prev.sections,
        {
          title: 'New Section',
          subtitle: 'Subtitle',
          icon: 'file',
          questions: []
        }
      ]
    }));
  };

  const handleDeleteSection = (sectionIndex) => {
    setFaqData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, idx) => idx !== sectionIndex)
    }));
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  if (loading) {
    return <Spinner text="Loading pages..." />;
  }

  return (
    <div className="admin-pages">
      <div className="pages-header">
        <h1>Content Management</h1>
        <p>Manage static pages: About, FAQ, and Contact</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="pages-layout">
        <div className="pages-sidebar">
          <h3>Pages</h3>
          <div className="pages-list">
            {pages.map(page => (
              <div
                key={page.id}
                className={`page-item ${selectedPage?.id === page.id ? 'active' : ''}`}
                onClick={() => handleSelectPage(page)}
              >
                <div className="page-info">
                  <div className="page-title">{page.title}</div>
                  <div className="page-meta">
                    <span className="page-slug">/{page.slug}</span>
                    <span className={`page-status ${page.published ? 'published' : 'draft'}`}>
                      {page.published ? '✓ Published' : '✗ Draft'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pages-editor">
          {selectedPage ? (
            <>
              <div className="editor-toolbar">
                <h2>{isEditing ? 'Editing' : 'Viewing'}: {selectedPage.title}</h2>
                <div className="toolbar-actions">
                  {!isEditing ? (
                    <button onClick={handleEdit} className="btn btn-primary">
                      Edit Page
                    </button>
                  ) : (
                    <>
                      <button onClick={handleCancel} className="btn btn-secondary">
                        Cancel
                      </button>
                      <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="editor-form">
                <div className="form-group">
                  <label>Page Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>URL Slug</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="form-input"
                  />
                  <small>URL: /{formData.slug}</small>
                </div>

                <div className="form-group">
                  <label>Meta Description (SEO)</label>
                  <textarea
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="form-textarea"
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label>Content</label>
                  {selectedPage.content_type === 'faq_json' ? (
                    // FAQ Editor
                    <div className="faq-editor">
                      {!isEditing ? (
                        <div className="faq-preview">
                          {faqData?.sections.map((section, sectionIdx) => (
                            <div key={sectionIdx} className="faq-section-preview">
                              <h3>{section.subtitle} - {section.title} ({section.icon} icon)</h3>
                              <ul>
                                {section.questions.map((q, qIdx) => (
                                  <li key={qIdx}>
                                    <strong>Q:</strong> {q.question}<br />
                                    <strong>A:</strong> {q.answer}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="faq-edit-form">
                          {faqData?.sections.map((section, sectionIdx) => (
                            <div key={sectionIdx} className="faq-section-editor">
                              <div className="section-header">
                                <h3>Section {sectionIdx + 1}</h3>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSection(sectionIdx)}
                                  className="btn-delete-section"
                                  disabled={faqData.sections.length === 1}
                                >
                                  Delete Section
                                </button>
                              </div>

                              <div className="form-row">
                                <div className="form-col">
                                  <label>Subtitle</label>
                                  <input
                                    type="text"
                                    value={section.subtitle}
                                    onChange={(e) => handleFaqSectionChange(sectionIdx, 'subtitle', e.target.value)}
                                    className="form-input"
                                  />
                                </div>
                                <div className="form-col">
                                  <label>Title</label>
                                  <input
                                    type="text"
                                    value={section.title}
                                    onChange={(e) => handleFaqSectionChange(sectionIdx, 'title', e.target.value)}
                                    className="form-input"
                                  />
                                </div>
                                <div className="form-col-small">
                                  <label>Icon</label>
                                  <select
                                    value={section.icon}
                                    onChange={(e) => handleFaqSectionChange(sectionIdx, 'icon', e.target.value)}
                                    className="form-input"
                                  >
                                    <option value="file">File</option>
                                    <option value="gear">Gear</option>
                                  </select>
                                </div>
                              </div>

                              <div className="faq-questions">
                                <h4>Questions</h4>
                                {section.questions.map((question, qIdx) => (
                                  <div key={qIdx} className="faq-question-editor">
                                    <div className="question-header">
                                      <span>Question {qIdx + 1}</span>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteQuestion(sectionIdx, qIdx)}
                                        className="btn-delete-question"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                    <input
                                      type="text"
                                      placeholder="Question"
                                      value={question.question}
                                      onChange={(e) => handleFaqQuestionChange(sectionIdx, qIdx, 'question', e.target.value)}
                                      className="form-input"
                                    />
                                    <textarea
                                      placeholder="Answer"
                                      value={question.answer}
                                      onChange={(e) => handleFaqQuestionChange(sectionIdx, qIdx, 'answer', e.target.value)}
                                      className="form-textarea"
                                      rows="3"
                                    />
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => handleAddQuestion(sectionIdx)}
                                  className="btn btn-secondary"
                                >
                                  + Add Question
                                </button>
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={handleAddSection}
                            className="btn btn-primary"
                            style={{ marginTop: '20px' }}
                          >
                            + Add Section
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Regular WYSIWYG Editor
                    <>
                      {isEditing ? (
                        <div className="editor-with-preview">
                          <div className="editor-panel">
                            <h4>Editor</h4>
                            <ReactQuill
                              theme="snow"
                              value={formData.content}
                              onChange={handleContentChange}
                              modules={quillModules}
                              className="wysiwyg-editor"
                            />
                          </div>
                          <div className="preview-panel">
                            <h4>Live Preview</h4>
                            <div
                              className="preview-content"
                              dangerouslySetInnerHTML={{ __html: formData.content }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="content-readonly" dangerouslySetInnerHTML={{ __html: formData.content }} />
                      )}
                    </>
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="published"
                      checked={formData.published}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                    <span>Published (visible to customers)</span>
                  </label>
                </div>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>Select a page from the sidebar to edit</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPages;
